import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import DynamicHead from "@/components/DynamicHead";
import { useNlProvinces } from "@/hooks/useServicePlaces";
import { STATIC_PATHS, servicePath, type Lang } from "@/lib/services";
import { ui } from "@/lib/siteText";
import { SITE_CONFIG } from "@/lib/constants";
import { ContactSection, Kicker, NlSiteLink, PhpAlert, ServiceGrid, telHref } from "@/components/site/SiteBlocks";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import PublishedSections from "@/components/site/PublishedSections";
import { FaqBlock } from "@/components/site/SiteBlocks";
import { usePageSections } from "@/hooks/usePageContent";

const META: Record<Lang, { title: string; desc: string }> = {
  nl: { title: "Website beveiliging, cybersecurity & netwerken in heel Nederland", desc: "Website gehackt of verouderde PHP? Cyscom helpt met website-beveiliging, nieuwe websites, cyber scans, firewalls en bedrijfsnetwerken in heel Nederland." },
  en: { title: "Website security, cybersecurity & networks across the Netherlands", desc: "Hacked website or outdated PHP? Cyscom helps with website security, new websites, cyber scans, firewalls and business networks across the Netherlands." },
  fr: { title: "Sécurité web, cybersécurité et réseaux aux Pays-Bas", desc: "Site piraté ou PHP obsolète ? Cyscom vous aide : sécurité de site web, nouveaux sites, scans, pare-feu et réseaux d'entreprise aux Pays-Bas." },
};

export default function Home({ lang }: { lang: Lang }) {
  const t = ui(lang);
  const { data: provinces } = useNlProvinces();
  const { data: content } = useWebsiteContent(STATIC_PATHS.home[lang]);
  const { data: sections } = usePageSections("home");
  const hero = sections?.hero;
  return (
    <>
      <DynamicHead pageTitle={content?.metaTitle || META[lang].title} pageDescription={content?.metaDescription || META[lang].desc} canonicalPath={STATIC_PATHS.home[lang]} alternates={STATIC_PATHS.home} />
      <section className="relative overflow-hidden border-b border-border">
        <div className="site-grid pointer-events-none absolute inset-0 opacity-40" />
        <div className="container relative py-20 md:py-28">
          <Kicker>{t.heroKicker}</Kicker>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold leading-[1.05] md:text-6xl">{content?.title || hero?.title || t.heroTitle}</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">{content?.intro || hero?.content || t.heroText}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to={servicePath("website-gehackt", lang)}>{t.urgent} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={telHref}><Phone className="mr-2 h-4 w-4" />{SITE_CONFIG.phone}</a>
            </Button>
          </div>
        </div>
      </section>
      <PublishedSections content={content} />
      {content && <section className="container py-10"><FaqBlock lang={lang} items={content.faq} /></section>}

      <div className="container py-10">
        <PhpAlert lang={lang} />
      </div>

      <section className="container pb-16">
        <h2 className="font-display text-3xl font-semibold">{t.allServices}</h2>
        <div className="mt-6">
          <ServiceGrid lang={lang} />
        </div>
      </section>

      {provinces && (
        <section className="border-t border-border">
          <div className="container py-14">
            <h2 className="font-display text-2xl font-semibold">{t.byProvince}</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {provinces.map((p) => (
                <Link key={p.id} to={servicePath("cybersecurity", lang, p.slug)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary">
                  {p.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactSection lang={lang} subject="Contact via homepage cyscom.eu" />
      <NlSiteLink lang={lang} />
    </>
  );
}
